import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Card,
  CardContent,
  IconButton,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Collapse,
  TextField,
  Autocomplete,
} from "@mui/material";
import { ArrowUpward, ArrowDownward, Settings, Visibility, VisibilityOff, Close, Star, Edit, ExpandMore, ExpandLess } from "@mui/icons-material";
import { useState, useEffect, useCallback, useRef } from "react";
import type { ChartConfig } from "@hooks/use-reports/types";
import type { AddedChartEntry } from "@hooks/use-report-layout";
import { moveItemInArray, toggleItemVisibility } from "@utils/array-utils";
import { getUserGeneratedHighlightBg } from "../reporting-constants";
import { useGetAggregateAssets } from "@hooks/use-get/aggregate-assets";
import { countDistinctAggregateValues } from "@hooks/use-get/aggregate-assets/utils";
import { isDateField } from "@hooks/use-reports/utils/date-utils";

const MAX_FILTER_FIELD_DISTINCT_VALUES = 50;

interface LayoutItem {
  id: string;
  order: number;
  hidden: boolean;
}

interface ReportLayoutModalProps {
  open: boolean;
  onClose: () => void;
  allCharts: readonly ChartConfig[];
  allStatCards: readonly any[];
  currentLayout: {
    charts: LayoutItem[];
    statCards: LayoutItem[];
  };
  onSave: (
    draftLayout: { charts: LayoutItem[]; statCards: LayoutItem[] },
    settingsDraft?: { title: string; description: string; filterFields: string[] }
  ) => void;
  onReset?: () => void;
  onOpenAddChartModal: () => void;
  onOpenAddStatCardModal: () => void;
  isSaving?: boolean;
  saveError?: string | null;
  hasCustomizations?: boolean;
  additionalStatCards?: Array<{ id: string; config: any }>;
  onDeleteAddedStatCard?: (cardId: string) => void;
  onEditAddedStatCard?: (cardId: string, config: any) => void;
  additionalCharts?: AddedChartEntry[];
  onDeleteAddedChart?: (chartId: string) => void;
  onEditAddedChart?: (chartId: string, config: ChartConfig) => void;
  /** When provided, a "Report Settings" section is shown for user-generated reports */
  reportSettings?: {
    title: string;
    description: string;
    filterFields: string[];
    availableFilterFields: Array<{ id: string; label: string }>;
    /** Active data source used to query distinct value counts for filter fields */
    dataSource?: "Assets" | "Tickets" | "Users";
  };
}

/**
 * Modal for editing report layout (show/hide, reorder).
 * Uses internal draft state - changes only apply when "Save" is clicked.
 */
export const ReportLayoutModal = ({
  open,
  onClose,
  allCharts,
  allStatCards,
  currentLayout,
  onSave,
  onReset,
  onOpenAddChartModal,
  onOpenAddStatCardModal,
  isSaving = false,
  saveError = null,
  hasCustomizations = false,
  additionalStatCards = [],
  onDeleteAddedStatCard = () => {},
  onEditAddedStatCard = () => {},
  additionalCharts = [],
  onDeleteAddedChart = () => {},
  onEditAddedChart = () => {},
  reportSettings,
}: ReportLayoutModalProps) => {
  // Sort items by order property when initializing
  const [draftLayout, setDraftLayout] = useState(() => ({
    charts: [...currentLayout.charts].sort((a, b) => a.order - b.order),
    statCards: [...currentLayout.statCards].sort((a, b) => a.order - b.order),
  }));

  // Report settings draft state (only used when reportSettings prop is provided)
  const [settingsDraft, setSettingsDraft] = useState({
    title: reportSettings?.title ?? "",
    description: reportSettings?.description ?? "",
    filterFields: reportSettings?.filterFields ?? [] as string[],
  });
  const [showSettings, setShowSettings] = useState(false);

  // Filter field count-fetching state
  const { getAggregateAssets } = useGetAggregateAssets();
  const [filterFieldCounts, setFilterFieldCounts] = useState<Record<string, number>>({});
  const [filterFieldCountsLoading, setFilterFieldCountsLoading] = useState(false);
  const fetchedFieldsKeyRef = useRef("");

  const fetchFilterFieldCounts = useCallback(async () => {
    if (!reportSettings?.availableFilterFields.length) return;
    const fieldsKey = reportSettings.availableFilterFields.map((f) => f.id).join(",");
    if (fetchedFieldsKeyRef.current === fieldsKey) return;
    fetchedFieldsKeyRef.current = fieldsKey;
    setFilterFieldCountsLoading(true);
    const postType = reportSettings.dataSource === "Tickets" ? "ticket" : "asset";
    const results = await Promise.allSettled(
      reportSettings.availableFilterFields.map(async ({ id }) => {
        const result = await getAggregateAssets({ groupBy: [id], postType });
        return { id, count: countDistinctAggregateValues(result.data, id) };
      })
    );
    const counts: Record<string, number> = {};
    results.forEach((r) => {
      if (r.status === "fulfilled") counts[r.value.id] = r.value.count;
    });
    setFilterFieldCounts(counts);
    setFilterFieldCountsLoading(false);
  }, [getAggregateAssets, reportSettings]);

  // Sync settings draft when modal opens
  useEffect(() => {
    if (open && reportSettings) {
      setSettingsDraft({
        title: reportSettings.title,
        description: reportSettings.description,
        filterFields: [...reportSettings.filterFields],
      });
    }
  }, [open, reportSettings]);

  // Reset the fetched-fields key when the modal closes so re-opening re-fetches if fields changed
  useEffect(() => {
    if (!open) {
      fetchedFieldsKeyRef.current = "";
    }
  }, [open]);

  // Initialize draft when modal opens - ensure items are sorted by order
  useEffect(() => {
    if (open) {
      setDraftLayout({
        charts: [...currentLayout.charts].sort((a, b) => a.order - b.order),
        statCards: [...currentLayout.statCards].sort((a, b) => a.order - b.order),
      });
    }
  }, [open, currentLayout]);

  // Sync draftLayout when additionalCharts changes (new charts added/removed)
  useEffect(() => {
    if (open && additionalCharts.length > 0) {
      setDraftLayout((prev) => {
        const addedChartIds = new Set(additionalCharts.map((c) => c.id));
        const draftChartIds = new Set(prev.charts.map((c) => c.id));
        const newCharts = Array.from(addedChartIds).filter((id) => !draftChartIds.has(id));
        if (newCharts.length > 0) {
          let updatedCharts = [...prev.charts];
          newCharts.forEach((chartId) => {
            updatedCharts = [
              { id: chartId, order: 0, hidden: false },
              ...updatedCharts.map((chart) => ({ ...chart, order: chart.order + 1 })),
            ];
          });
          return { ...prev, charts: updatedCharts };
        }
        return prev;
      });
    }
  }, [open, additionalCharts]);

  // Sync draftLayout when additionalStatCards changes (new cards added/removed)
  useEffect(() => {
    if (open && additionalStatCards.length > 0) {
      setDraftLayout((prev) => {
        // Check if we need to add any new stat cards to draftLayout
        const addedCardIds = new Set(additionalStatCards.map((c) => c.id));
        const draftCardIds = new Set(prev.statCards.map((c) => c.id));
        
        // Find cards in additionalStatCards that aren't in draftLayout
        const newCards = Array.from(addedCardIds).filter((id) => !draftCardIds.has(id));
        
        if (newCards.length > 0) {
          let updatedStatCards = [...prev.statCards];
          newCards.forEach((cardId) => {
            updatedStatCards = [
              {
                id: cardId,
                order: 0,
                hidden: false,
              },
              ...updatedStatCards.map((card) => ({ ...card, order: card.order + 1 })),
            ];
          });
          return {
            ...prev,
            statCards: updatedStatCards,
          };
        }
        
        return prev;
      });
    }
  }, [open, additionalStatCards]);

  // Toggle chart visibility in draft
  const toggleChartVisibility = (chartId: string) => {
    setDraftLayout((prev) => ({
      ...prev,
      charts: toggleItemVisibility(prev.charts, chartId),
    }));
  };

  // Move chart up in draft
  const moveChartUp = (chartId: string) => {
    setDraftLayout((prev) => ({
      ...prev,
      charts: moveItemInArray(prev.charts, chartId, "up"),
    }));
  };

  // Move chart down in draft
  const moveChartDown = (chartId: string) => {
    setDraftLayout((prev) => ({
      ...prev,
      charts: moveItemInArray(prev.charts, chartId, "down"),
    }));
  };

  // Toggle stat card visibility in draft
  const toggleStatCardVisibility = (cardId: string) => {
    setDraftLayout((prev) => ({
      ...prev,
      statCards: toggleItemVisibility(prev.statCards, cardId),
    }));
  };

  // Move stat card up in draft
  const moveStatCardUp = (cardId: string) => {
    setDraftLayout((prev) => ({
      ...prev,
      statCards: moveItemInArray(prev.statCards, cardId, "up"),
    }));
  };

  // Move stat card down in draft
  const moveStatCardDown = (cardId: string) => {
    setDraftLayout((prev) => ({
      ...prev,
      statCards: moveItemInArray(prev.statCards, cardId, "down"),
    }));
  };

  const handleSave = () => {
    if (reportSettings && !settingsDraft.title.trim()) return;
    onSave(draftLayout, reportSettings ? settingsDraft : undefined);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Report Layout</DialogTitle>

      <DialogContent sx={{ maxHeight: "70vh", overflow: "auto" }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {saveError && (
            <Alert severity="error">{saveError}</Alert>
          )}

          {/* REPORT SETTINGS — only for user-generated reports */}
          {reportSettings && (
            <Box>
              <Button
                size="small"
                onClick={() => {
                  setShowSettings((v) => !v);
                  fetchFilterFieldCounts();
                }}
                sx={{ p: 0, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {showSettings ? <ExpandLess /> : <ExpandMore />}
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Report Settings</Typography>
              </Button>
              <Collapse in={showSettings} timeout="auto" unmountOnExit>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                  <TextField
                    label="Title"
                    value={settingsDraft.title}
                    onChange={(e) => setSettingsDraft((d) => ({ ...d, title: e.target.value }))}
                    fullWidth
                    size="small"
                    required
                    error={!settingsDraft.title.trim()}
                    helperText={!settingsDraft.title.trim() ? "Title is required" : undefined}
                  />
                  <TextField
                    label="Description"
                    value={settingsDraft.description}
                    onChange={(e) => setSettingsDraft((d) => ({ ...d, description: e.target.value }))}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                  />
                  {(() => {
                    const countsReady = Object.keys(filterFieldCounts).length > 0;
                    // Options: if counts loaded, only those with ≤50 distinct values (plus already-selected).
                    // Date fields are always included regardless of distinct value count.
                    const selectableFilterOptions = countsReady
                      ? reportSettings.availableFilterFields.filter(
                          (f) =>
                            settingsDraft.filterFields.includes(f.id) ||
                            isDateField(f.id) ||
                            (filterFieldCounts[f.id] ?? Infinity) <= MAX_FILTER_FIELD_DISTINCT_VALUES
                        )
                      : reportSettings.availableFilterFields;
                    return (
                      <>
                        <Autocomplete
                          multiple
                          loading={filterFieldCountsLoading}
                          options={selectableFilterOptions}
                          getOptionLabel={(o) => o.label}
                          value={selectableFilterOptions.filter((o) =>
                            settingsDraft.filterFields.includes(o.id)
                          )}
                          onChange={(_, selected) =>
                            setSettingsDraft((d) => ({ ...d, filterFields: selected.map((o) => o.id) }))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Filter Menu Options"
                              size="small"
                              helperText="Columns users can interactively filter when viewing the report"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {filterFieldCountsLoading && <CircularProgress size={14} sx={{ mr: 1 }} />}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                              const { key, ...tagProps } = getTagProps({ index });
                              return <Chip key={key} label={option.label} size="small" {...tagProps} />;
                            })
                          }
                        />
                      </>
                    );
                  })()}


                </Stack>
              </Collapse>
              <Divider sx={{ mt: 2 }} />
            </Box>
          )}

          {/* STAT CARDS SECTION */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Stat Cards
            </Typography>

            {draftLayout.statCards.length === 0 ? (
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
                No stat cards
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {draftLayout.statCards.map((card, idx) => {
                  // Find if this card is a custom card
                  const customCard = additionalStatCards?.find((c) => c.id === card.id);
                  const isCustom = Boolean(customCard);
                  const cardConfig = customCard?.config || allStatCards.find((c) => c.id === card.id);
                  
                  return (
                    <Card 
                      key={card.id} 
                      sx={{ 
                        bgcolor: (theme) => card.hidden 
                          ? theme.palette.action.disabledBackground 
                          : isCustom 
                          ? getUserGeneratedHighlightBg(theme) 
                          : theme.palette.background.paper,
                        borderLeft: isCustom ? "4px solid" : "none",
                        borderLeftColor: isCustom ? "info.main" : "transparent",
                      }}
                    >
                      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
                            {isCustom && <Star fontSize="small" sx={{ color: "info.main" }} />}
                            <Typography variant="body2" sx={{ fontWeight: isCustom ? 600 : 400 }}>
                              {cardConfig?.label || cardConfig?.title || card.id}
                            </Typography>
                          </Box>
                          
                          <IconButton
                            size="small"
                            onClick={() => toggleStatCardVisibility(card.id)}
                            title={card.hidden ? "Show" : "Hide"}
                          >
                            {card.hidden ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                          
                          {isCustom && (
                            <IconButton
                              size="small"
                              onClick={() => onEditAddedStatCard(card.id, customCard?.config)}
                              title="Edit custom stat card"
                              color="info"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                          
                          <IconButton size="small" onClick={() => moveStatCardUp(card.id)} disabled={idx === 0}>
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveStatCardDown(card.id)}
                            disabled={idx === draftLayout.statCards.length - 1}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                          
                          {isCustom && (
                            <IconButton
                              size="small"
                              onClick={() => onDeleteAddedStatCard(card.id)}
                              color="error"
                              title="Delete custom stat card"
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}

            <Button
              size="small"
              variant="outlined"
              startIcon={<Settings />}
              onClick={onOpenAddStatCardModal}
              fullWidth
            >
              Add New Stat Card
            </Button>
          </Box>

          {/* CHARTS SECTION */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Charts
            </Typography>

            {draftLayout.charts.length === 0 ? (
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
                No charts
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {draftLayout.charts.map((chart, idx) => {
                  const customChart = additionalCharts?.find((c) => c.id === chart.id);
                  const isCustom = Boolean(customChart);
                  const chartConfig = customChart?.config || allCharts.find((c) => c.id === chart.id);

                  return (
                    <Card
                      key={chart.id}
                      sx={{
                        bgcolor: (theme) => chart.hidden
                          ? theme.palette.action.disabledBackground
                          : isCustom
                          ? getUserGeneratedHighlightBg(theme)
                          : theme.palette.background.paper,
                        borderLeft: isCustom ? "4px solid" : "none",
                        borderLeftColor: isCustom ? "info.main" : "transparent",
                      }}
                    >
                      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
                            {isCustom && <Star fontSize="small" sx={{ color: "info.main" }} />}
                            <Typography variant="body2" sx={{ fontWeight: isCustom ? 600 : 400 }}>
                              {chartConfig?.title || chart.id}
                            </Typography>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={() => toggleChartVisibility(chart.id)}
                            title={chart.hidden ? "Show" : "Hide"}
                          >
                            {chart.hidden ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>

                          {isCustom && (
                            <IconButton
                              size="small"
                              onClick={() => onEditAddedChart(chart.id, customChart!.config)}
                              title="Edit custom chart"
                              color="info"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}

                          <IconButton size="small" onClick={() => moveChartUp(chart.id)} disabled={idx === 0}>
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveChartDown(chart.id)}
                            disabled={idx === draftLayout.charts.length - 1}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>

                          {isCustom && (
                            <IconButton
                              size="small"
                              onClick={() => onDeleteAddedChart(chart.id)}
                              color="error"
                              title="Delete custom chart"
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}

            <Button
              size="small"
              variant="outlined"
              startIcon={<Settings />}
              onClick={onOpenAddChartModal}
              fullWidth
            >
              Add New Chart
            </Button>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        {hasCustomizations && (
          <Button 
            onClick={onReset} 
            variant="outlined" 
            color="warning"
            disabled={isSaving}
            sx={{ mr: "auto" }}
          >
            Reset to Admin Settings
          </Button>
        )}
        <Button onClick={onClose} variant="outlined" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isSaving}
          endIcon={isSaving ? <CircularProgress size={20} /> : undefined}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
